import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { InputField } from "@/components/ui/input-field";
import { useVerifyResetToken } from "@/queries/use-verify-reset-token";
import { useResetPassword } from "@/queries/use-reset-password";
import { ResetPasswordSchema } from "@/schemas/auth";
import { AUTH_ROUTES } from "@/utils/navigation";

export const Route = createFileRoute("/(auth)/reset-password/")({
  validateSearch: z.object({
    reset_password_token: z.string().optional().catch(""),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/reset-password" });
  const { reset_password_token } = Route.useSearch();

  const { isError: isTokenInvalid, isLoading: isVerifyingToken } =
    useVerifyResetToken(reset_password_token || "");

  const form = useForm({
    resolver: zodResolver(ResetPasswordSchema()),
    defaultValues: {
      password: "",
      password_confirmation: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const { mutate: resetPassword, isPending } = useResetPassword({
    onSuccess: () => navigate({ to: AUTH_ROUTES.SIGN_IN }),
    onError: (error) => console.error("Password reset error:", error),
  });

  useEffect(() => {
    if (isTokenInvalid) {
      navigate({ to: AUTH_ROUTES.FORGOT_PASSWORD });
    }
  }, [isTokenInvalid, navigate]);

  const onSubmit = (data: {
    password: string;
    password_confirmation: string;
  }) => {
    if (!reset_password_token || isVerifyingToken) return;

    resetPassword({
      token: reset_password_token,
      payload: {
        password: data.password,
        password_confirmation: data.password_confirmation,
      },
    });
  };

  return (
    <div className="flex w-full max-w-[262px] flex-col items-center md:max-w-[340px] md:items-start lg:max-w-[400px] 4k:max-w-[1061px]">
      <h2 className="text-center text-[20px] font-bold text-gray-800 md:text-left md:text-2xl lg:text-[32px] 4k:text-[80px] dark:text-white">
        {t("pages:reset-password.title")}
      </h2>

      <form
        className="mt-10 flex w-full flex-col md:mt-6 4k:w-[1061px]"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="mb-6 4k:mb-12">
          <InputField.Password
            id="password"
            type="password"
            label={t("generic:forms.fields.newPassword")}
            placeholder="**********"
            error={errors.password}
            {...register("password")}
          />
        </div>

        <InputField.Password
          id="password_confirmation"
          type="password"
          label={t("generic:forms.fields.confirmPassword")}
          placeholder="**********"
          error={errors.password_confirmation}
          {...register("password_confirmation")}
        />

        <div className="mt-14 md:mt-8 4k:mt-12">
          <Button
            type="submit"
            loading={isPending || isVerifyingToken}
            className="w-full 4k:h-fit 4k:px-[40px] 4k:py-[16px] 4k:text-[40px]"
          >
            {t("pages:reset-password.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
